/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { initializeTokenClient, loadGoogleScript } from "./googleClient";

interface AuthState {
  token: string | null;
  expiresAt: number | null;
  email?: string;
}

interface AuthContextValue extends AuthState {
  signIn: () => Promise<void>;
  signOut: () => void;
  loading: boolean;
  error?: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "mailsift_auth";
const GMAIL_SCOPE = "https://www.googleapis.com/auth/gmail.readonly";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, expiresAt: null };
    try {
      const parsed = JSON.parse(raw) as AuthState;
      if (parsed.expiresAt && parsed.expiresAt < Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return { token: null, expiresAt: null };
      }
      return parsed;
    } catch (err) {
      console.error(err);
      return { token: null, expiresAt: null };
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!state.token) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const signOut = () => {
    setState({ token: null, expiresAt: null, email: undefined });
    localStorage.removeItem(STORAGE_KEY);
  };

  const signIn = async () => {
    setLoading(true);
    setError(undefined);
    try {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      if (!clientId) {
        throw new Error("Missing Google Client ID. Set VITE_GOOGLE_CLIENT_ID in your .env file.");
      }
      await loadGoogleScript();
      const tokenClient = initializeTokenClient(clientId, GMAIL_SCOPE, (tokenResponse) => {
        const expiresAt = Date.now() + (tokenResponse.expires_in ?? 0) * 1000;
        setState({ token: tokenResponse.access_token, expiresAt });
      });
      tokenClient.requestAccessToken({ prompt: "consent" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({ ...state, signIn, signOut, loading, error }),
    [state, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
